import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "../../../contexts/ThemeContext";
import { apiClient } from "../../../api/client";
import { chartTypeMap } from "./Charts";
import { elasticQueryDataToLegacyChartShape } from "./elasticQueryToLegacyChartData";
import { chartStateShell } from "./Charts/chartTheme";

export type ElasticQueryListItem = {
  id: string;
  title: string;
  type: string;
};

/** Events Analytics toolbar only — never sent on analytic-index charts. */
export type EventsAnalyticsSectionFilters = Partial<{
  Events: string[];
  Status: string[];
  Slots: string[];
}>;

/** Analytic Graphs toolbar only — never sent on events-index charts. */
export type AnalyticGraphsSectionFilters = Partial<{
  Slots: string[];
  Classes: string[];
}>;

export type OverviewElasticFilterSection = "events-analytics" | "analytic-graphs";

function appendSectionFiltersToParams(
  section: OverviewElasticFilterSection,
  sectionFilters: EventsAnalyticsSectionFilters | AnalyticGraphsSectionFilters | undefined,
  params: Record<string, string | boolean>,
) {
  if (!sectionFilters) return;
  if (section === "events-analytics") {
    const f = sectionFilters as EventsAnalyticsSectionFilters;
    if (f.Events?.length) params.Events = JSON.stringify(f.Events);
    if (f.Status?.length) params.Status = JSON.stringify(f.Status);
    if (f.Slots?.length) params.Slots = JSON.stringify(f.Slots);
    return;
  }
  const f = sectionFilters as AnalyticGraphsSectionFilters;
  if (f.Slots?.length) params.Slots = JSON.stringify(f.Slots);
  if (f.Classes?.length) params.Classes = JSON.stringify(f.Classes);
}

function sectionFiltersCacheKey(
  section: OverviewElasticFilterSection,
  sectionFilters: EventsAnalyticsSectionFilters | AnalyticGraphsSectionFilters | undefined,
): string {
  if (!sectionFilters) return "none";
  if (section === "events-analytics") {
    const f = sectionFilters as EventsAnalyticsSectionFilters;
    return JSON.stringify({
      Events: f.Events?.length ? [...f.Events].sort() : null,
      Status: f.Status?.length ? [...f.Status].sort() : null,
      Slots: f.Slots?.length ? [...f.Slots].sort() : null,
    });
  }
  const f = sectionFilters as AnalyticGraphsSectionFilters;
  return JSON.stringify({
    Slots: f.Slots?.length ? [...f.Slots].sort() : null,
    Classes: f.Classes?.length ? [...f.Classes].sort() : null,
  });
}

type Props = {
  row: ElasticQueryListItem;
  clientId: string;
  fromIso: string;
  toIso: string;
  locationIds: string[];
  cameraIds: string[];
  /** Increment when user applies Smart Filters or this section's Apply (see parent). */
  applyGeneration: number;
  /** After dashboard filter sync succeeds, chart requests use skipFilterWrite. */
  filtersSynced: boolean;
  /** Which toolbar drives filters — controls which query params are allowed on `/surveillance/elastic-queries/data`. */
  filterSection: OverviewElasticFilterSection;
  /** Dropdown selections for this section only (Events Analytics vs Analytic Graphs UI). */
  sectionFilters?: EventsAnalyticsSectionFilters | AnalyticGraphsSectionFilters;
  xLabel?: string;
  yLabel?: string;
};

/**
 * Loads one saved elastic-query chart via GET `/surveillance/elastic-queries/data?queryId=…`
 * when the card scrolls into view (lazy REST, one request per chart).
 */
export function OverviewElasticChart({
  row,
  clientId,
  fromIso,
  toIso,
  locationIds,
  cameraIds,
  applyGeneration,
  filtersSynced,
  filterSection,
  sectionFilters,
  xLabel = "Time",
  yLabel = "Absolute",
}: Props) {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.12, rootMargin: "160px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const locKey = JSON.stringify(locationIds);
  const camKey = JSON.stringify(cameraIds);
  const sectionFiltersKey = sectionFiltersCacheKey(filterSection, sectionFilters);

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      "overview-elastic-chart",
      filterSection,
      row.id,
      clientId,
      applyGeneration,
      fromIso,
      toIso,
      locKey,
      camKey,
      sectionFiltersKey,
    ],
    enabled:
      Boolean(clientId) &&
      filtersSynced &&
      visible &&
      locationIds.length > 0 &&
      cameraIds.length > 0,
    queryFn: async () => {
      const params: Record<string, string | boolean> = {
        clientId,
        from: fromIso,
        to: toIso,
        locationIds: JSON.stringify(locationIds),
        cameraIds: JSON.stringify(cameraIds),
        queryId: row.id,
        skipFilterWrite: true,
      };
      appendSectionFiltersToParams(filterSection, sectionFilters, params);
      const res = await apiClient.get("/surveillance/elastic-queries/data", {
        params,
      });
      return res.data as {
        queries?: Array<{
          id: string;
          data?: unknown;
          error?: string;
          type?: string;
        }>;
      };
    },
  });

  const slice = data?.queries?.[0];
  const legacyData = useMemo(
    () => elasticQueryDataToLegacyChartShape(slice?.type ?? row.type, slice?.data),
    [slice?.type, slice?.data, row.type],
  );

  const ChartComponent = chartTypeMap[row.type] ?? chartTypeMap.pie;

  const hasRenderableData = useMemo(() => {
    if (!slice || slice.error) return false;
    const d = slice.data;
    return Array.isArray(d) && d.length > 0;
  }, [slice]);

  return (
    <div ref={containerRef} className="relative flex min-h-[320px] flex-col">
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3 className="min-w-0 text-sm font-semibold text-slate-800 dark:text-slate-100">{row.title}</h3>
        <span className="shrink-0 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {row.type}
        </span>
      </div>

      {slice?.error && (
        <div className={`${chartStateShell} flex min-h-[240px] items-center justify-center text-sm text-rose-600 dark:text-rose-400`}>
          {slice.error}
        </div>
      )}

      {!slice?.error && isFetching && (
        <div className={`${chartStateShell} flex min-h-[240px] items-center justify-center`}>
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500 dark:border-slate-600" />
        </div>
      )}

      {!slice?.error && !isFetching && !hasRenderableData && (
        <div className={`${chartStateShell} flex min-h-[240px] items-center justify-center text-sm text-slate-600 dark:text-slate-400`}>
          No data for this range.
        </div>
      )}

      {!slice?.error && !isFetching && hasRenderableData && ChartComponent && (
        <div className="min-h-[300px] flex-1">
          <ChartComponent
            title={row.title}
            type={row.type}
            xLabel={xLabel}
            yLabel={yLabel}
            legend
            data={legacyData}
            isFetching={false}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {isError && (
        <div className={`${chartStateShell} text-sm text-rose-600 dark:text-rose-400`}>
          Could not load this chart.
        </div>
      )}
    </div>
  );
}
